import { useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Barcode, MagnifyingGlass, ArrowRight, Camera, WarningCircle, MapPin } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import api, { money, formatApiErrorDetail } from "../lib/api";
import { PROJECT_STATUSES } from "./Projects";

const READER_ID = "project-scan-reader";
const STATUS_STYLE = {
  "Not Started": "bg-zinc-100 text-zinc-600 border-zinc-200",
  "In Progress": "bg-blue-50 text-blue-700 border-blue-200",
  "Completed": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "On Hold": "bg-amber-50 text-amber-700 border-amber-200",
};

export function ProjectScanner({ open, onOpenChange, projects, onOpenProject }) {
  const scannerRef = useRef(null);
  const [manual, setManual] = useState("");
  const [result, setResult] = useState(null); // project | "notfound" | null
  const [camError, setCamError] = useState("");
  const [scanning, setScanning] = useState(false);
  const qc = useQueryClient();
  const [savingStatus, setSavingStatus] = useState(false);

  const stopCamera = async () => {
    const s = scannerRef.current;
    scannerRef.current = null;
    if (s) {
      try { await s.stop(); } catch { /* already stopped */ }
      try { await s.clear(); } catch { /* ignore */ }
    }
    setScanning(false);
  };

  const lookup = (raw) => {
    const code = (raw || "").trim();
    if (!code) return;
    const p = projects.find((x) => (x.code || "").toLowerCase() === code.toLowerCase());
    setResult(p || "notfound");
    if (p) stopCamera();
  };

  useEffect(() => {
    if (!open) return;
    setResult(null); setCamError(""); setManual("");
    let cancelled = false;
    const start = async () => {
      try {
        const scanner = new Html5Qrcode(READER_ID, {
          formatsToSupport: [Html5QrcodeSupportedFormats.CODE_128, Html5QrcodeSupportedFormats.QR_CODE],
          verbose: false,
        });
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 280, height: 160 } },
          (decodedText) => { lookup(decodedText); },
          () => {},
        );
        if (cancelled) { stopCamera(); return; }
        setScanning(true);
      } catch (e) {
        setCamError("Camera unavailable or permission denied. Type the project code below instead.");
      }
    };
    const t = setTimeout(start, 250);
    return () => { cancelled = true; clearTimeout(t); stopCamera(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = (v) => { if (!v) stopCamera(); onOpenChange(v); };

  // record a scan event (with best-effort geolocation) whenever a project is matched
  useEffect(() => {
    if (!result || result === "notfound") return;
    let done = false;
    const post = (lat, lng, accuracy) => {
      if (done) return; done = true;
      api.post(`/projects/${result.id}/scan`, { lat, lng, accuracy, source: "scan" })
        .then(() => qc.invalidateQueries({ queryKey: ["project-scans", result.id] }))
        .catch(() => {});
    };
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => post(pos.coords.latitude, pos.coords.longitude, pos.coords.accuracy),
        () => post(null, null, null),
        { timeout: 4000, maximumAge: 60000 },
      );
    } else {
      post(null, null, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result]);

  const updateStatus = async (newStatus) => {
    if (!result || result === "notfound") return;
    setSavingStatus(true);
    try {
      await api.put(`/projects/${result.id}`, { status: newStatus });
      setResult((r) => ({ ...r, status: newStatus }));
      qc.invalidateQueries({ queryKey: ["projects"] });
      qc.invalidateQueries({ queryKey: ["project", result.id] });
      toast.success(`Status updated to ${newStatus}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update status");
    } finally {
      setSavingStatus(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="rounded-sm max-w-md" data-testid="project-scan-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Barcode size={20} className="text-primary" /> Scan Project Barcode</DialogTitle>
          <DialogDescription className="sr-only">Scan or type a project barcode to look up its current status.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {!result && (
            <>
              <div className="relative rounded-sm overflow-hidden border border-border bg-black/90 aspect-video flex items-center justify-center">
                <div id={READER_ID} className="w-full" data-testid="project-scan-reader" />
                {!scanning && !camError && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-white/70 text-sm gap-2 pointer-events-none">
                    <Camera size={28} /> Starting camera…
                  </div>
                )}
              </div>
              {camError && (
                <p className="flex items-start gap-2 text-xs text-amber-600" data-testid="project-scan-cam-error"><WarningCircle size={15} className="mt-0.5 shrink-0" /> {camError}</p>
              )}
              {!camError && <p className="text-xs text-muted-foreground text-center">Point your camera at a project's Code128 barcode. Works on desktop webcams and phones.</p>}

              <div className="flex items-center gap-2 pt-1">
                <Input
                  data-testid="project-scan-manual-input"
                  value={manual} onChange={(e) => setManual(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") lookup(manual); }}
                  placeholder="…or type a project code (e.g. PRJ-00001)"
                  className="rounded-sm font-mono text-sm"
                />
                <Button onClick={() => lookup(manual)} data-testid="project-scan-lookup" className="rounded-sm bg-primary hover:bg-primary/90 shrink-0">
                  <MagnifyingGlass size={16} weight="bold" />
                </Button>
              </div>
            </>
          )}

          {result === "notfound" && (
            <div className="text-center py-6" data-testid="project-scan-notfound">
              <WarningCircle size={34} className="mx-auto mb-3 text-amber-500" />
              <p className="text-sm font-medium">No project matches that code.</p>
              <Button variant="outline" onClick={() => { setResult(null); onOpenChange(false); setTimeout(() => onOpenChange(true), 50); }} data-testid="project-scan-retry" className="rounded-sm mt-4">Scan again</Button>
            </div>
          )}

          {result && result !== "notfound" && (
            <div className="space-y-4" data-testid="project-scan-result">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-mono text-xs text-muted-foreground">{result.code}</p>
                  <h3 className="text-lg font-bold tracking-tight">{result.name}</h3>
                </div>
                <span className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold border rounded-sm ${STATUS_STYLE[result.status] || STATUS_STYLE["Not Started"]}`} data-testid="project-scan-status">{result.status}</span>
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm border border-border bg-secondary/40 p-3 rounded-sm">
                <div><p className="overline-label">Customer</p><p className="font-medium">{result.customer_name || "—"}</p></div>
                <div><p className="overline-label">Manager</p><p className="font-medium">{result.manager || "—"}</p></div>
                <div><p className="overline-label">Start</p><p className="font-medium font-mono text-xs">{result.start_date || "—"}</p></div>
                <div><p className="overline-label">End</p><p className="font-medium font-mono text-xs">{result.end_date || "—"}</p></div>
                <div className="col-span-2"><p className="overline-label">Budget</p><p className="font-semibold tabular">{money(result.budget)}</p></div>
              </div>
              <div>
                <p className="overline-label mb-1">Update status</p>
                <Select value={result.status} onValueChange={updateStatus} disabled={savingStatus}>
                  <SelectTrigger className="rounded-sm" data-testid="project-scan-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PROJECT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">Change the project's state right here — no need to open it.</p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setResult(null)} data-testid="project-scan-again" className="rounded-sm flex-1">Scan another</Button>
                <Button onClick={() => { onOpenChange(false); onOpenProject(result.id); }} data-testid="project-scan-open" className="rounded-sm bg-primary hover:bg-primary/90 flex-1 group">
                  Open project <ArrowRight size={15} className="ml-1 transition-transform group-hover:translate-x-0.5" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
